import { type SearchModuleTranslationKey } from '@pkgs/components/types/typedTranslationKeys';
import { type ComponentTFunction, type TFunction } from '@pkgs/components/providers/AppContextProvider';
import {
  type Currency,
  type Language,
  type MeasurementSystem,
  type PricesV2,
  PropertyMarketingType,
  type SearchListing,
  type SearchUserAccountExclusiveListing,
  UNIT_SYMBOL_MAP,
} from '@ev-tech/search-modules-api';

import { containsTruthyRangeValue } from './searchStateUtils';
import { getRentPrice } from './propertyDataUtils';
import { formatNumber, formatPriceWithoutDecimals } from './numberFormatters';
import { getListingPlotArea, getListingRooms } from './listingDataUtils';
import { type ObjectTypeCategory } from '../hooks/stores/search/slices/objectTypeFilterSlice';
import { type Attribute } from '../components/SearchResultCard/DefaultSearchResultCard';

type PropertyCardAttributeTranslationKey = Extract<SearchModuleTranslationKey, `search.expose.propertyDetails.${string}`>;
type PriceLabelTranslationKey = Extract<SearchModuleTranslationKey, `search.price.label.${string}`>;

export const getSpecificLivingAreaLabel = (isDevelopment: boolean): PropertyCardAttributeTranslationKey => {
  if (isDevelopment) {
    return 'search.expose.propertyDetails.development.label.livingArea';
  }

  return 'search.expose.propertyDetails.label.livingArea';
};

type StringifyCardAttributeParams = {
  label: string;
  value: string | number;
  language: Language;
  reverse?: boolean;
  measurementSystem?: string;
  shouldShowRoundedValue?: boolean;
};

export const stringifyCardAttribute = ({
  label,
  value,
  language,
  reverse = false,
  measurementSystem,
  shouldShowRoundedValue,
}: StringifyCardAttributeParams): string => {
  let _value = value;
  if (measurementSystem) {
    const formattedNumber = formatNumber(
      { number: typeof _value === 'number' ? _value : Number.parseFloat(_value), language, shouldShowRoundedValue },
      { maximumFractionDigits: shouldShowRoundedValue ? 0 : 1 },
    );
    _value = `${formattedNumber} ${measurementSystem}`;
  }

  return reverse ? `${label} ${_value}` : `${_value} ${label}`;
};

export type PropertyAttribute = {
  id: string;
  label: string;
  value?: number | string;
  unitSymbol?: string;
  reverse?: boolean;
  shouldShowRoundedValue?: boolean;
};

export function refineAttributeList(attributes: PropertyAttribute[], language: Language, isDevelopment = false): Attribute[] {
  return attributes.reduce<Attribute[]>((prev, curr) => {
    if (!curr.value) {
      return [...prev];
    }
    const label = isDevelopment
      ? ` ${curr.label}`
      : stringifyCardAttribute({
          label: curr.label,
          value: curr.value,
          language,
          reverse: curr.reverse,
          measurementSystem: curr.unitSymbol,
          shouldShowRoundedValue: curr.shouldShowRoundedValue,
        });

    return [
      ...prev,
      {
        id: curr.id,
        label,
      },
    ];
  }, []);
}

export function getDevelopmentCardAttributes({
  property,
  t,
  language,
  measurementSystem,
}: {
  property: SearchListing;
  t: ComponentTFunction;
  language: Language;
  measurementSystem: MeasurementSystem;
}) {
  const { livingSurface, totalSurface } = property.area ?? {};
  const { id, bedrooms, bathrooms, rooms, totalUnits, isDevelopmentProject } = property;
  const plotArea = getListingPlotArea(property, { language, measurementSystem });
  const livingAreaLabel = getSpecificLivingAreaLabel(isDevelopmentProject);
  const livingAreaDevelopmentValue = containsTruthyRangeValue(livingSurface, true)
    ? formatNumber({ number: livingSurface.min, language, shouldShowRoundedValue: true }, { maximumFractionDigits: 0 })
    : undefined;
  const plotSurfaceDevelopmentValue = plotArea?.value;
  const totalSurfaceDevelopmentValue = containsTruthyRangeValue(totalSurface, true)
    ? formatNumber({ number: totalSurface.min, language, shouldShowRoundedValue: true }, { maximumFractionDigits: 0 })
    : undefined;

  const unitSymbol = UNIT_SYMBOL_MAP[measurementSystem];

  const attributes: PropertyAttribute[] = [];

  if (containsTruthyRangeValue(bedrooms, true)) {
    attributes.push({
      id: `${id}-bedrooms`,
      label: t('search.expose.propertyDetails.development.label.bedrooms', {
        count: bedrooms.min,
        bedrooms: bedrooms.min,
      }),
      value: formatNumber({ number: bedrooms.min, language }, { maximumFractionDigits: 2 }),
    });
  }

  if (containsTruthyRangeValue(bathrooms, true)) {
    attributes.push({
      id: `${id}-bathrooms`,
      label: t('search.expose.propertyDetails.development.label.bathrooms', {
        count: bathrooms.min,
        bathrooms: bathrooms.min,
      }),
      value: formatNumber({ number: bathrooms.min, language }, { maximumFractionDigits: 2 }),
    });
  }

  if (livingAreaDevelopmentValue !== '') {
    attributes.push({
      id: `${id}-livingArea`,
      label: `${t(livingAreaLabel, {
        livingArea: `${livingAreaDevelopmentValue} ${unitSymbol}`,
      })}`,
      value: livingAreaDevelopmentValue || 0,
    });
  }

  if (plotSurfaceDevelopmentValue) {
    attributes.push({
      id: `${id}-plotSurface`,
      label: `${t('search.expose.propertyDetails.development.label.plotSurface', {
        plotSurface: plotSurfaceDevelopmentValue,
      })}`,
      value: plotSurfaceDevelopmentValue,
    });
  }

  if (totalSurfaceDevelopmentValue !== '') {
    attributes.push({
      id: `${id}-totalSurface`,
      label: `${t('search.expose.propertyDetails.development.label.totalSurface', {
        totalSurface: `${totalSurfaceDevelopmentValue} ${unitSymbol}`,
      })}`,
      value: totalSurfaceDevelopmentValue || 0,
    });
  }

  if (totalUnits !== undefined && totalUnits > 0) {
    attributes.push({
      id: `${id}-units`,
      label: t(`search.expose.propertyDetails.developmentProject.label.units`, {
        units: totalUnits,
        count: totalUnits,
      }),
      value: totalUnits,
    });
  }

  if (containsTruthyRangeValue(rooms, true)) {
    attributes.unshift({
      id: `${id}-rooms`,
      label: t('search.expose.propertyDetails.development.label.rooms', {
        count: rooms.min,
        rooms: rooms.min,
      }),
      value: formatNumber({ number: rooms.min, language }, { maximumFractionDigits: 2 }),
    });
  }

  return refineAttributeList(attributes, language, true);
}

export function getCommercialCardAttributes({
  property,
  t,
  language,
  measurementSystem,
}: {
  property: SearchListing;
  t: ComponentTFunction;
  language: Language;
  measurementSystem: MeasurementSystem;
}) {
  const livingAreaLabel = getSpecificLivingAreaLabel(property.isDevelopmentProject);

  const unitSymbol = UNIT_SYMBOL_MAP[measurementSystem];

  const plotArea = getListingPlotArea(property, { language, measurementSystem });

  const { livingSurface, totalSurface, dividableFrom } = property.area ?? {};

  const attributes: PropertyAttribute[] = [];

  if (containsTruthyRangeValue(livingSurface, true) || containsTruthyRangeValue(totalSurface, true)) {
    attributes.push({
      id: `${property.id}-livingArea`,
      label: livingSurface ? t(livingAreaLabel) : t('search.expose.propertyDetails.label.totalSurface'),
      value: livingSurface?.min || totalSurface?.min || 0,
      unitSymbol,
      shouldShowRoundedValue: true,
    });
  }

  if (plotArea) {
    attributes.push({
      id: `${property.id}-plotSurface`,
      label: t(plotArea.labelKey),
      value: plotArea.value,
    });
  }

  if (containsTruthyRangeValue(dividableFrom, true)) {
    attributes.push({
      id: `${property.id}-dividableFrom`,
      label: t('search.expose.propertyDetails.label.dividableFrom'),
      value: dividableFrom.min,
      unitSymbol,
      reverse: true,
      shouldShowRoundedValue: true,
    });
  }

  return refineAttributeList(attributes, language);
}

const getRoomAttribute = ({
  property,
  t,
  language,
}: {
  property: SearchListing | SearchUserAccountExclusiveListing;
  t: ComponentTFunction;
  language: Language;
}): PropertyAttribute | undefined => {
  const room = getListingRooms(property, { language });
  if (!room) return;
  return {
    id: `${property.id}-${room.fieldName}`,
    label: t(room.labelKey, {
      count: room.labelCount,
    }),
    value: room.value,
  };
};
// TODO: refactor to not duplicate code with getCommercialCardAttributes
export function getResidentialCardAttributes({
  property,
  t,
  language,
  measurementSystem,
}: {
  property: SearchListing;
  t: ComponentTFunction;
  language: Language;
  measurementSystem: MeasurementSystem;
}): Attribute[] {
  const plotArea = getListingPlotArea(property, { language, measurementSystem });

  const { livingSurface, totalSurface } = property.area ?? {};
  const unitSymbol = UNIT_SYMBOL_MAP[measurementSystem];

  const livingAreaLabel = getSpecificLivingAreaLabel(property.isDevelopmentProject);
  const attributes: PropertyAttribute[] = [];

  const roomOrBedroomAttribute = getRoomAttribute({ property, t, language });

  if (roomOrBedroomAttribute) {
    attributes.push(roomOrBedroomAttribute);
  }

  if (containsTruthyRangeValue(property.bathrooms, true)) {
    attributes.push({
      id: `${property.id}-bathrooms`,
      label: t('search.expose.propertyDetails.label.bathrooms', {
        count: property.bathrooms.min,
      }),
      value: formatNumber({ number: property.bathrooms.min, language }, { maximumFractionDigits: 2 }),
    });
  }

  if (containsTruthyRangeValue(livingSurface, true) || containsTruthyRangeValue(totalSurface, true)) {
    attributes.push({
      id: `${property.id}-livingArea`,
      label: livingSurface ? t(livingAreaLabel) : t('search.expose.propertyDetails.label.totalSurface'),
      value: livingSurface?.min || totalSurface?.min || 0,
      unitSymbol,
      shouldShowRoundedValue: true,
    });
  }

  if (plotArea) {
    attributes.push({
      id: `${property.id}-${plotArea.fieldName.slice('area.'.length)}`,
      label: t(plotArea.labelKey),
      value: plotArea.value,
    });
  }

  return refineAttributeList(attributes, language);
}

export function getUserExclusiveCardAttributes({
  property,
  t,
  language,
  measurementSystem,
}: {
  property: SearchUserAccountExclusiveListing;
  t: ComponentTFunction;
  language: Language;
  measurementSystem: MeasurementSystem;
}): Attribute[] {
  const { livingSurface, plotSurface, totalSurface } = property.area ?? {};
  const unitSymbol = UNIT_SYMBOL_MAP[measurementSystem];
  const attributes: PropertyAttribute[] = [];

  const roomOrBedroomAttribute = getRoomAttribute({ property, t, language });

  if (roomOrBedroomAttribute) {
    attributes.push(roomOrBedroomAttribute);
  }

  if (containsTruthyRangeValue(property.bathrooms, true)) {
    attributes.push({
      id: `${property.id}-bathrooms`,
      label: t('search.expose.propertyDetails.label.bathrooms', {
        count: property.bathrooms.min,
      }),
      value: formatNumber({ number: property.bathrooms.min, language }, { maximumFractionDigits: 2 }),
    });
  }

  if (containsTruthyRangeValue(livingSurface, true) || containsTruthyRangeValue(totalSurface, true)) {
    attributes.push({
      id: `${property.id}-livingArea`,
      label: livingSurface ? t('search.expose.propertyDetails.label.livingArea') : t('search.expose.propertyDetails.label.totalSurface'),
      value: livingSurface?.min || totalSurface?.min || 0,
      unitSymbol,
      shouldShowRoundedValue: true,
    });
  }

  if (containsTruthyRangeValue(plotSurface, true)) {
    attributes.push({
      id: `${property.id}-plotSurface`,
      label: t('search.expose.propertyDetails.label.plotSurface'),
      value: plotSurface.min,
      unitSymbol,
      shouldShowRoundedValue: true,
    });
  }

  return refineAttributeList(attributes, language);
}

export type GetCardAttributesParams = {
  objectTypeCategory: ObjectTypeCategory;
  listing: SearchListing;
  translationResolver: TFunction;
  language: Language;
  measurementSystem: MeasurementSystem;
};

export const getCardAttributes = ({
  objectTypeCategory,
  listing,
  translationResolver,
  language,
  measurementSystem,
}: GetCardAttributesParams): Attribute[] => {
  const typedTFunction = translationResolver as ComponentTFunction;
  switch (objectTypeCategory) {
    case 'commercial': {
      return getCommercialCardAttributes({ property: listing, t: typedTFunction, language, measurementSystem });
    }
    case 'residential': {
      return getResidentialCardAttributes({ property: listing, t: typedTFunction, language, measurementSystem });
    }
    case 'development': {
      return getDevelopmentCardAttributes({ property: listing, t: typedTFunction, language, measurementSystem });
    }
    default: {
      return [];
    }
  }
};

export type GetCardPriceParams = {
  listing: SearchListing;
  prices: PricesV2;
  language: Language;
  currency: Currency;
};

export const getCardPrice = ({
  listing,
  prices,
  language,
  currency,
}: GetCardPriceParams): { value: string; labelTranslationKey?: PriceLabelTranslationKey } => {
  if (listing.isDevelopmentProject) {
    return {
      value: formatPriceWithoutDecimals(prices.salesPrice?.min || 0, language, currency),
      labelTranslationKey: 'search.price.label.development.price',
    };
  }
  if (listing.propertyMarketingType === PropertyMarketingType.sale) {
    return {
      value: formatPriceWithoutDecimals(prices.salesPrice?.min || 0, language, currency),
    };
  }

  return getRentPrice(listing, prices, language, currency);
};

export type VtourType = 'videoTour' | 'virtualTour' | 'multimedia';

export const getVirtualTourType = (vtourUrlNormalized?: string, videoUrlNormalized?: string): VtourType | undefined => {
  if (vtourUrlNormalized && videoUrlNormalized) {
    return 'multimedia';
  } else if (vtourUrlNormalized) {
    return 'virtualTour';
  } else if (videoUrlNormalized) {
    return 'videoTour';
  }
  return undefined;
};
